import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Grid,
    Group,
    Image,
    Loader,
    Paper,
    Select,
    Stack,
    Text,
    Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title as ChartTitle,
    Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import React from "react";
import { FaSync } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { GET_ALL_OAUTHS } from "../../services/oauths";
import { SYNC_INSTAGRAM_ANALYTICS } from "../../services/instagram_analytics";
import SimpleHeader from "../../components/simple_header";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Filler,
    ChartTitle,
    Tooltip,
    Legend
);

export const instagramSections = [
    { key: "overview", title: "Visão geral", path: "" },
    { key: "media", title: "Mídias", path: "/media" },
    { key: "growth", title: "Crescimento", path: "/growth" },
    { key: "engagement", title: "Engajamento", path: "/engagement" },
    { key: "best-posts", title: "Melhores posts", path: "/best-posts" },
    { key: "audience", title: "Audiência", path: "/audience" },
];

export const formatPercent = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "-";
    return `${(value * 100).toFixed(2)}%`;
};

export const formatNumber = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "-";
    return value.toLocaleString("pt-BR");
};

export const formatDate = (value?: string) => {
    if (!value) return "-";
    return dayjs(value).format("DD/MM/YYYY");
};

export const getDefaultDateRange = (days = 30) => {
    return [dayjs().subtract(days, "day").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")];
};

export const parseMaybeNumber = (value: any) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const pickMetric = (source: any, candidates: string[]) => {
    for (const key of candidates) {
        const value = parseMaybeNumber(source?.[key]);
        if (value !== null) return value;
    }
    return null;
};

export const normalizeInstagramMetrics = (source: any) => {
    return {
        impressions: pickMetric(source, ["impressions", "impression", "impressions_count"]),
        reach: pickMetric(source, ["reach", "reach_count"]),
        likes: pickMetric(source, ["likes", "like_count", "likes_count"]),
        comments: pickMetric(source, ["comments", "comment_count", "comments_count"]),
        saves: pickMetric(source, ["saves", "saved", "saved_count", "save_count"]),
        shares: pickMetric(source, ["shares", "share_count", "shares_count"]),
        plays: pickMetric(source, ["plays", "video_views", "video_view_count", "views"]),
    };
};

export const resolveMediaLatestMetrics = (media: any) => {
    const history = Array.isArray(media?.metrics_history) ? media.metrics_history : [];
    const fromHistory = history.length > 0 ? history[history.length - 1] : null;

    const source =
        media?.latest_metrics
        || media?.metrics
        || media?.insights
        || media?.metric_values
        || fromHistory
        || {};

    return normalizeInstagramMetrics(source);
};

export const normalizeMetricsHistory = (rows: any[]) => {
    return (rows || []).map((item: any) => {
        const normalized = normalizeInstagramMetrics(item);
        return {
            ...item,
            ...normalized,
            date: item?.date || item?.day || item?.timestamp || item?.created_at,
        };
    });
};

export const extractGrowthRows = (response: any) => {
    const rows =
        response?.growth
        || response?.followers_growth
        || response?.series
        || response?.data?.growth
        || response?.data?.followers_growth
        || [];

    return (rows || []).map((item: any) => ({
        ...item,
        date: item?.date || item?.day || item?.timestamp || item?.created_at,
        followers_count: pickMetric(item, ["followers_count", "followers", "count"]) || 0,
    }));
};

export const isOverviewEmpty = (overview: any) => {
    if (!overview) return true;
    const hasFollowers = parseMaybeNumber(overview.followers_current) !== null;
    const hasImpressions = parseMaybeNumber(overview.total_impressions) !== null;
    const hasReach = parseMaybeNumber(overview.total_reach) !== null;
    const hasGrowth = (overview.followers_growth ?? []).length > 0;
    return !hasFollowers && !hasImpressions && !hasReach && !hasGrowth;
};

export const AnalyticsEmptyState = ({
    oauthId,
    syncing,
    onSync,
    message = "Sem dados ainda. Sincronize para começar.",
}: {
    oauthId?: string;
    syncing?: boolean;
    onSync: () => void;
    message?: string;
}) => {
    return (
        <Flex mih={360} justify="center" align="center" direction="column" rowGap="sm">
            <Image maw={220} src="/assets/aprovaai/EmptyState.png" alt="Sem dados" />
            <Title order={4}>{message}</Title>
            <Button
                leftIcon={<FaSync />}
                loading={!!syncing}
                disabled={!!syncing || !oauthId}
                onClick={onSync}
                size="md"
            >
                Sincronizar dados do Instagram
            </Button>
        </Flex>
    );
};

export const useInstagramAccounts = () => {
    const [accounts, setAccounts] = React.useState<any[]>([]);
    const [loadingAccounts, setLoadingAccounts] = React.useState<boolean>(true);

    React.useEffect(() => {
        setLoadingAccounts(true);
        GET_ALL_OAUTHS()
            .then((oauths) => {
                setAccounts((oauths ?? []).filter((item) => item.origin === "instagram"));
            })
            .catch((err) => {
                showNotification({ message: err.message, color: "red" });
            })
            .finally(() => {
                setLoadingAccounts(false);
            });
    }, []);

    return { accounts, loadingAccounts };
};

export const useInstagramSync = (oauthId?: string, onAfterSync?: () => Promise<void> | void) => {
    const [syncing, setSyncing] = React.useState<boolean>(false);
    const [cooldown, setCooldown] = React.useState<boolean>(false);

    const triggerSync = async () => {
        if (!oauthId || syncing || cooldown) return;

        try {
            setSyncing(true);
            setCooldown(true);

            await SYNC_INSTAGRAM_ANALYTICS(oauthId);
            showNotification({ message: "Sincronização iniciada em segundo plano...", color: "blue" });

            setTimeout(async () => {
                if (!onAfterSync) return;
                await onAfterSync();
            }, 10000);

            setTimeout(() => {
                setCooldown(false);
                setSyncing(false);
            }, 15000);
        } catch (err: any) {
            setSyncing(false);
            setCooldown(false);
            showNotification({ message: err.message, color: "red" });
        }
    };

    return { syncing: syncing || cooldown, triggerSync };
};

export const InstagramAnalyticsHeader = ({
    oauthId,
    title,
    subtitle,
    active,
    accounts,
    loadingAccounts,
    syncing,
    onSync,
}: {
    oauthId?: string;
    title: string;
    subtitle?: string;
    active: string;
    accounts: any[];
    loadingAccounts?: boolean;
    syncing?: boolean;
    onSync: () => void;
}) => {
    const navigate = useNavigate();

    const accountOptions = (accounts ?? []).map((account) => ({
        value: account._id,
        label: account?.data?.me?.name || account?.data?.me?.username || account._id,
    }));

    return (
        <Stack mb="md" spacing="sm">
            <SimpleHeader
                title={title}
                subtitle={subtitle}
                right={
                    <Group position="right">
                        <Select
                            searchable
                            clearable={false}
                            data={accountOptions}
                            placeholder="Selecione a conta do Instagram"
                            value={oauthId || null}
                            onChange={(value) => {
                                if (!value) return;
                                const section = instagramSections.find((item) => item.key === active);
                                const suffix = section?.path || "";
                                navigate(`/analytics/instagram/${value}${suffix}`);
                            }}
                            disabled={loadingAccounts || (accounts ?? []).length === 0}
                            rightSection={loadingAccounts ? <Loader size="xs" /> : undefined}
                            w={260}
                        />
                        <Button leftIcon={<FaSync />} onClick={onSync} loading={!!syncing} disabled={!oauthId || !!syncing}>
                            Sincronizar agora
                        </Button>
                    </Group>
                }
            />

            <Paper p="xs" withBorder>
                <Group spacing="xs">
                    {instagramSections.map((section) => {
                        const href = oauthId ? `/analytics/instagram/${oauthId}${section.path}` : "#";
                        const selected = section.key === active;
                        return (
                            <Button
                                key={section.key}
                                component={oauthId ? Link : "button"}
                                to={oauthId ? href : undefined}
                                variant={selected ? "filled" : "light"}
                                disabled={!oauthId}
                                size="xs"
                            >
                                {section.title}
                            </Button>
                        );
                    })}
                </Group>
            </Paper>
        </Stack>
    );
};

export const StatCard = ({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) => {
    return (
        <Card withBorder>
            <Text size="sm" c="dimmed">
                {label}
            </Text>
            <Text size="xl" fw={700} c={color || "dark"}>
                {value}
            </Text>
        </Card>
    );
};

export const MediaTypeBadge = ({ type }: { type?: string }) => {
    const color =
        type === "REEL" ? "violet" : type === "VIDEO" ? "blue" : type === "IMAGE" ? "green" : "gray";
    return <Badge color={color}>{type || "DESCONHECIDO"}</Badge>;
};

export const parseAudienceEntries = (record?: Record<string, number>) => {
    return Object.entries(record || {})
        .map(([label, value]) => ({ label, value: Number(value || 0) }))
        .sort((a, b) => b.value - a.value);
};

export const getSyncResultMessage = (stillEmpty: boolean) => {
    if (!stillEmpty) return;
    showNotification({
        message: "Sincronização iniciada. Os dados podem levar alguns minutos para aparecer.",
        color: "yellow",
    });
};

export const getSectionPath = (oauthId: string, section: string) => {
    const match = instagramSections.find((item) => item.key === section);
    return `/analytics/instagram/${oauthId}${match?.path || ""}`;
};

export const CardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="sm">
                {title}
            </Text>
            <Box>{children}</Box>
        </Paper>
    );
};
